import { Injectable } from "@angular/core";
import { BehaviorSubject, Subject } from "rxjs";
import {
  AnyElement,
  CanvasProject,
  Slide,
  Position,
  Size,
  TextElement,
  ImageElement,
  ShapeElement,
  TableElement,
  TableCellStyle,
  TableCellSelection,
} from "../types/canvas.types";
import { ImageUrlService } from "./image-url.service";

@Injectable({
  providedIn: "root",
})
export class CanvasService {
  private readonly CANVAS_WIDTH = 800;
  private readonly CANVAS_HEIGHT = 600;
  private readonly MIN_ZOOM = 0.05;
  private readonly MAX_ZOOM = 3;

  private projectSubject = new BehaviorSubject<CanvasProject | null>(null);
  private selectedElementSubject = new BehaviorSubject<AnyElement | null>(null);
  private currentSlideSubject = new BehaviorSubject<Slide | null>(null);
  private selectedTableCellSubject =
    new BehaviorSubject<TableCellSelection | null>(null);
  private zoomSubject = new BehaviorSubject<number>(1);
  private fitRequestSubject = new Subject<void>();

  project$ = this.projectSubject.asObservable();
  selectedElement$ = this.selectedElementSubject.asObservable();
  currentSlide$ = this.currentSlideSubject.asObservable();
  selectedTableCell$ = this.selectedTableCellSubject.asObservable();
  zoom$ = this.zoomSubject.asObservable();
  fitRequest$ = this.fitRequestSubject.asObservable();

  constructor(private imageUrlService: ImageUrlService) {}

  readonly canvasDimensions = {
    width: this.CANVAS_WIDTH,
    height: this.CANVAS_HEIGHT,
  } as const;

  get minZoom(): number {
    return this.MIN_ZOOM;
  }

  get maxZoom(): number {
    return this.MAX_ZOOM;
  }

  get zoom(): number {
    return this.zoomSubject.value;
  }

  setZoom(level: number): void {
    const clamped = this.clampZoom(level);
    if (clamped !== this.zoomSubject.value) {
      this.zoomSubject.next(clamped);
    }
  }

  zoomIn(step = 0.1): void {
    this.setZoom(this.zoom + step);
  }

  zoomOut(step = 0.1): void {
    this.setZoom(this.zoom - step);
  }

  resetZoom(): void {
    this.setZoom(1);
  }

  requestFitToViewport(): void {
    this.fitRequestSubject.next();
  }

  fitZoomToViewport(width: number, height: number): number {
    if (width <= 0 || height <= 0) {
      return this.zoom;
    }

    const scale = Math.min(
      width / this.CANVAS_WIDTH,
      height / this.CANVAS_HEIGHT
    );
    const clamped = this.clampZoom(scale);
    this.setZoom(clamped);
    return clamped;
  }

  createProject(name: string): CanvasProject {
    // Create first slide with title and subtitle
    const firstSlide = this.createSlide("Slide 1");

    // Add title text element
    const titleElement: TextElement = {
      id: this.generateId(),
      type: "text",
      content: "Title",
      fontSize: 48,
      fontFamily: "Arial",
      fontWeight: "bold",
      color: "#000000",
      textAlign: "center",
      position: { x: 200, y: 150 },
      size: { width: 400, height: 80 },
      zIndex: 1,
      bold: true,
    };

    // Add subtitle text element
    const subtitleElement: TextElement = {
      id: this.generateId(),
      type: "text",
      content: "Sub-title",
      fontSize: 24,
      fontFamily: "Arial",
      fontWeight: "normal",
      color: "#666666",
      textAlign: "center",
      position: { x: 200, y: 250 },
      size: { width: 400, height: 50 },
      zIndex: 2,
      bold: false,
    };

    firstSlide.elements.push(titleElement, subtitleElement);

    // Create second slide (empty)
    const secondSlide = this.createSlide("Slide 2");

    const project: CanvasProject = {
      id: this.generateId(),
      name,
      slides: [firstSlide, secondSlide],
      currentSlideIndex: 0,
      createdAt: new Date(),
      updatedAt: new Date(),
    };

    this.projectSubject.next(project);
    this.currentSlideSubject.next(project.slides[0]);
    return project;
  }

  createProjectIfNotExists(name: string): CanvasProject | null {
    const currentProject = this.projectSubject.value;
    if (currentProject) {
      return currentProject;
    }
    return this.createProject(name);
  }

  createSlide(name: string): Slide {
    return {
      id: this.generateId(),
      name,
      elements: [],
      background: "#ffffff",
    };
  }

  addSlide(name: string): void {
    const project = this.projectSubject.value;
    if (!project) return;

    const newSlide = this.createSlide(name);
    project.slides.push(newSlide);
    project.updatedAt = new Date();

    this.publishProjectState(project);
  }

  switchSlide(index: number): void {
    const project = this.projectSubject.value;
    if (!project || index < 0 || index >= project.slides.length) return;

    project.currentSlideIndex = index;
    this.selectedElementSubject.next(null);
    this.publishProjectState(project);
  }

  reorderSlides(fromIndex: number, toIndex: number): void {
    const project = this.projectSubject.value;
    if (!project || fromIndex === toIndex) return;

    if (
      fromIndex < 0 ||
      fromIndex >= project.slides.length ||
      toIndex < 0 ||
      toIndex >= project.slides.length
    ) {
      return;
    }

    // Remove the slide from the original position
    const [movedSlide] = project.slides.splice(fromIndex, 1);
    // Insert it at the new position
    project.slides.splice(toIndex, 0, movedSlide);

    // Update currentSlideIndex if needed
    if (project.currentSlideIndex === fromIndex) {
      // The currently active slide was moved
      project.currentSlideIndex = toIndex;
    } else if (
      project.currentSlideIndex > fromIndex &&
      project.currentSlideIndex <= toIndex
    ) {
      // Current slide was shifted left due to the move
      project.currentSlideIndex--;
    } else if (
      project.currentSlideIndex < fromIndex &&
      project.currentSlideIndex >= toIndex
    ) {
      // Current slide was shifted right due to the move
      project.currentSlideIndex++;
    }

    project.updatedAt = new Date();
    this.publishProjectState(project);
  }

  addElement(element: AnyElement): void {
    const project = this.projectSubject.value;
    if (!project) return;

    const currentSlide = project.slides[project.currentSlideIndex];
    element.zIndex = this.getNextZIndex(currentSlide);
    currentSlide.elements.push(element);

    project.updatedAt = new Date();
    this.publishProjectState(project);
  }

  updateElement(elementId: string, updates: Partial<AnyElement>): void {
    const project = this.projectSubject.value;
    if (!project) return;

    const currentSlide = project.slides[project.currentSlideIndex];
    const elementIndex = currentSlide.elements.findIndex(
      (el) => el.id === elementId
    );

    if (elementIndex >= 0) {
      const element = currentSlide.elements[elementIndex];
      let updatedElement: AnyElement;

      if (element.type === "text") {
        updatedElement = {
          ...element,
          ...updates,
        } as TextElement;
      } else if (element.type === "image") {
        updatedElement = {
          ...element,
          ...updates,
        } as ImageElement;
      } else if (element.type === "shape") {
        updatedElement = {
          ...element,
          ...updates,
        } as ShapeElement;
      } else if (element.type === "table") {
        updatedElement = {
          ...element,
          ...updates,
        } as TableElement;
      } else {
        return;
      }

      currentSlide.elements[elementIndex] = updatedElement;

      // Update the selected element if it's the same element being updated
      const selectedElement = this.selectedElementSubject.value;
      if (selectedElement && selectedElement.id === elementId) {
        this.selectedElementSubject.next(updatedElement);
      }

      project.updatedAt = new Date();
      this.publishProjectState(project);
    }
  }

  deleteElement(elementId: string): void {
    const project = this.projectSubject.value;
    if (!project) return;

    const currentSlide = project.slides[project.currentSlideIndex];
    currentSlide.elements = currentSlide.elements.filter(
      (el) => el.id !== elementId
    );

    project.updatedAt = new Date();
    this.selectedElementSubject.next(null);
    this.publishProjectState(project);
  }

  selectElement(element: AnyElement | null): void {
    this.selectedElementSubject.next(element);
    if (!element || element.type !== "table") {
      this.selectedTableCellSubject.next(null);
    }
  }

  selectTableCell(selection: TableCellSelection | null): void {
    this.selectedTableCellSubject.next(selection);
  }

  updateTableCellStyle(
    elementId: string,
    row: number,
    col: number,
    updates: Partial<TableCellStyle>
  ): void {
    const project = this.projectSubject.value;
    if (!project) return;

    const currentSlide = project.slides[project.currentSlideIndex];
    const elementIndex = currentSlide.elements.findIndex(
      (el) => el.id === elementId
    );

    if (elementIndex === -1) return;

    const element = currentSlide.elements[elementIndex];
    if (element.type !== "table") return;

    const table = element as TableElement;
    if (!table.cellStyles[row] || !table.cellStyles[row][col]) return;

    const newCellStyles = table.cellStyles.map((rowStyles, rIdx) =>
      rowStyles.map((style, cIdx) =>
        rIdx === row && cIdx === col ? { ...style, ...updates } : style
      )
    );

    const updatedTable: TableElement = {
      ...table,
      cellStyles: newCellStyles,
    };

    currentSlide.elements[elementIndex] = updatedTable;

    const selectedCell = this.selectedTableCellSubject.value;
    if (selectedCell && selectedCell.elementId === elementId) {
      this.selectedTableCellSubject.next({ ...selectedCell });
    }

    project.updatedAt = new Date();
    this.selectedElementSubject.next(updatedTable);
    this.publishProjectState(project);
  }

  bringForward(elementId: string): void {
    const project = this.projectSubject.value;
    if (!project) return;

    const currentSlide = project.slides[project.currentSlideIndex];
    const element = currentSlide.elements.find((el) => el.id === elementId);

    if (element) {
      const maxZIndex = Math.max(
        ...currentSlide.elements.map((el) => el.zIndex)
      );
      if (element.zIndex < maxZIndex) {
        // Find the next higher z-index and swap
        const higherElements = currentSlide.elements
          .filter((el) => el.zIndex > element.zIndex)
          .sort((a, b) => a.zIndex - b.zIndex);

        if (higherElements.length > 0) {
          const nextElement = higherElements[0];
          const tempZIndex = element.zIndex;
          element.zIndex = nextElement.zIndex;
          nextElement.zIndex = tempZIndex;
        }

        project.updatedAt = new Date();
        this.publishProjectState(project);
      }
    }
  }

  sendBackward(elementId: string): void {
    const project = this.projectSubject.value;
    if (!project) return;

    const currentSlide = project.slides[project.currentSlideIndex];
    const element = currentSlide.elements.find((el) => el.id === elementId);

    if (element) {
      const minZIndex = Math.min(
        ...currentSlide.elements.map((el) => el.zIndex)
      );
      if (element.zIndex > minZIndex) {
        // Find the next lower z-index and swap
        const lowerElements = currentSlide.elements
          .filter((el) => el.zIndex < element.zIndex)
          .sort((a, b) => b.zIndex - a.zIndex);

        if (lowerElements.length > 0) {
          const nextElement = lowerElements[0];
          const tempZIndex = element.zIndex;
          element.zIndex = nextElement.zIndex;
          nextElement.zIndex = tempZIndex;
        }
      }

      project.updatedAt = new Date();
      this.publishProjectState(project);
    }
  }

  exportToJSON(): string {
    const project = this.projectSubject.value;
    return project ? JSON.stringify(project, null, 2) : "";
  }

  exportForAPI(): string {
    const project = this.projectSubject.value;
    if (!project) return "";

    // Create a deep copy of the project to avoid modifying the original
    const projectCopy: CanvasProject = JSON.parse(JSON.stringify(project));

    // Transform all image URLs to paths
    projectCopy.slides = projectCopy.slides.map((slide) => ({
      ...slide,
      elements: slide.elements.map((element) => {
        if (element.type === "image") {
          const imageElement = element as ImageElement;
          return {
            ...imageElement,
            url: this.imageUrlService.extractImagePath(imageElement.url),
          };
        }
        return element;
      }),
    }));

    return JSON.stringify(projectCopy, null, 2);
  }

  loadFromAPI(jsonString: string): boolean {
    try {
      let project: CanvasProject;

      // Handle both string and already parsed objects
      if (typeof jsonString === "string") {
        project = JSON.parse(jsonString);
      } else if (typeof jsonString === "object" && jsonString !== null) {
        project = jsonString as CanvasProject;
      } else {
        throw new Error("Invalid input type for loadFromAPI");
      }

      // Validate project structure
      if (!project.id || !project.slides || !Array.isArray(project.slides)) {
        throw new Error(
          "Invalid project format - missing id, slides, or slides is not array"
        );
      }

      // Transform all image paths back to full URLs
      project.slides = project.slides.map((slide) => ({
        ...slide,
        elements: slide.elements.map((element) => {
          if (element.type === "image") {
            const imageElement = element as ImageElement;
            return {
              ...imageElement,
              url: this.imageUrlService.resolveImageUrl(imageElement.url),
            };
          }
          return element;
        }),
      }));

      // Ensure currentSlideIndex is valid
      if (
        typeof project.currentSlideIndex !== "number" ||
        project.currentSlideIndex < 0 ||
        project.currentSlideIndex >= project.slides.length
      ) {
        project.currentSlideIndex = 0;
      }

      // Add createdAt if missing
      if (!project.createdAt) {
        project.createdAt = new Date();
      }

      // Add updatedAt if missing
      if (!project.updatedAt) {
        project.updatedAt = new Date();
      }

      this.selectedElementSubject.next(null);
      this.publishProjectState(project);

      return true;
    } catch (error) {
      console.error("Failed to load project from API:", error);
      return false;
    }
  }

  loadFromJSON(jsonString: string): boolean {
    try {
      let project: CanvasProject;

      // Handle both string and already parsed objects
      if (typeof jsonString === "string") {
        project = JSON.parse(jsonString);
      } else if (typeof jsonString === "object" && jsonString !== null) {
        project = jsonString as CanvasProject;
      } else {
        throw new Error("Invalid input type for loadFromJSON");
      }

      // Validate project structure
      if (!project.id || !project.slides || !Array.isArray(project.slides)) {
        throw new Error(
          "Invalid project format - missing id, slides, or slides is not array"
        );
      }

      // Ensure currentSlideIndex is valid
      if (
        typeof project.currentSlideIndex !== "number" ||
        project.currentSlideIndex < 0 ||
        project.currentSlideIndex >= project.slides.length
      ) {
        project.currentSlideIndex = 0;
      }

      this.selectedElementSubject.next(null);
      this.publishProjectState(project);

      return true;
    } catch (error) {
      console.error("Failed to load project:", error);
      return false;
    }
  }

  private cloneElement(element: AnyElement): AnyElement {
    const base: AnyElement = {
      ...element,
      position: element.position ? { ...element.position } : element.position,
      size: element.size ? { ...element.size } : element.size,
    } as AnyElement;

    if (element.type === "shape" && element.linePoints) {
      (base as ShapeElement).linePoints = {
        start: { ...element.linePoints.start },
        end: { ...element.linePoints.end },
      };
    }

    if (element.type === "table") {
      const tableElement = element as TableElement;
      (base as TableElement).cellData = tableElement.cellData.map((row) => [
        ...row,
      ]);
      (base as TableElement).cellStyles = tableElement.cellStyles.map((row) =>
        row.map((style) => ({ ...style }))
      );
    }

    return base;
  }

  private cloneSlide(slide: Slide): Slide {
    return {
      ...slide,
      elements: slide.elements.map((element) => this.cloneElement(element)),
    };
  }

  private publishProjectState(project: CanvasProject): void {
    const slides = project.slides.map((slide) => this.cloneSlide(slide));
    const hasSlides = slides.length > 0;
    const currentSlideIndex = hasSlides
      ? Math.min(Math.max(project.currentSlideIndex, 0), slides.length - 1)
      : 0;

    const updatedProject: CanvasProject = {
      ...project,
      slides,
      currentSlideIndex,
    };

    this.projectSubject.next(updatedProject);

    const currentSlide = hasSlides ? slides[currentSlideIndex] : null;
    this.currentSlideSubject.next(currentSlide);

    const selectedElementId = this.selectedElementSubject.value?.id ?? null;
    if (selectedElementId) {
      const locatedInCurrent = currentSlide?.elements.find(
        (element: AnyElement) => element.id === selectedElementId
      );

      const locatedElement = locatedInCurrent
        ? locatedInCurrent
        : slides.reduce<AnyElement | null>((found, slide: Slide) => {
            if (found) {
              return found;
            }
            const match = slide.elements.find(
              (element: AnyElement) => element.id === selectedElementId
            );
            return match ?? null;
          }, null);

      this.selectedElementSubject.next(
        locatedElement ? this.cloneElement(locatedElement) : null
      );
    }
  }

  private getNextZIndex(slide: Slide): number {
    if (slide.elements.length === 0) return 1;
    return Math.max(...slide.elements.map((el) => el.zIndex)) + 1;
  }

  private generateId(): string {
    return Math.random().toString(36).substr(2, 9);
  }

  private clampZoom(value: number): number {
    if (!isFinite(value)) {
      return this.zoomSubject.value;
    }
    return Math.min(this.MAX_ZOOM, Math.max(this.MIN_ZOOM, value));
  }
}
